/**
 * music-video-scene.js — "is this moment worth cutting to?" for /music-video-maker.
 *
 * Report #1344 ("нет оценки сцены") is about the montage picking its moments by
 * arithmetic alone: planSegments() spaces windows evenly and never looks at what
 * is IN them, so a cut lands on the clip's black first frame or a frozen stretch
 * exactly as readily as on the action.
 *
 * This module answers only that question, and only with material already on the
 * page: it samples the clip through the shared window.__videoThumbFallback
 * (HTML5 seek → FFmpeg ladder, memoised per File, returns ImageData already
 * downscaled) and scores each sample. It does NOT decode video itself — the
 * extraction layer is solved, see WebCodecsFrameExtractor / __probeKeyframes.
 *
 * planSegments() then spreads its windows over the LIVE material instead of over
 * wall-clock time. A profile is an optimisation, never a requirement: with none,
 * every entry point falls back to the arithmetic spread the tool always used.
 *
 *   await MusicVideoScene.profile(clip, {slug, isMobile}) → profile | null
 *   MusicVideoScene.qualityAt(profile, tSec) → 0..1
 *   MusicVideoScene.summary(profile) → { samples, dead, usableFrac }
 *
 * A `profile` is { dur, samples: [{t, luma, contrast, activity, q}] } with q in
 * 0..1: 0 = unusable (black, blown out, or flat), 1 = bright, textured, moving.
 */
(function () {
'use strict';
// `root` rather than a bare `window` so the pure scoring half of this file can
// be required from node for threshold tests (see the export tail).
var root = typeof window !== 'undefined' ? window : globalThis;
if (root.MusicVideoScene) return;

function ae(name, payload) { try { root.__ae && root.__ae(name, payload); } catch (_) {} }

// Samples to take across a clip: dense enough to find a good frame inside a
// window, cheap enough that a phone is not seeking for a minute.
function sampleCount(dur) {
    return Math.max(10, Math.min(36, Math.round(dur / 2) || 10));
}

/* Per-frame statistics from an ImageData. Rec.601 luma on a subsampled grid —
   the frames are already ~120 px wide, so every 2nd pixel is plenty. */
function stats(imageData) {
    var d = imageData && imageData.data;
    if (!d || !d.length) return null;
    var n = 0, sum = 0, sumSq = 0, grid = [];
    var w = imageData.width, h = imageData.height;
    var stepX = Math.max(1, Math.round(w / 24)), stepY = Math.max(1, Math.round(h / 14));
    for (var y = 0; y < h; y += stepY) {
        for (var x = 0; x < w; x += stepX) {
            var p = (y * w + x) * 4;
            var l = 0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2];
            grid.push(l); sum += l; sumSq += l * l; n++;
        }
    }
    if (!n) return null;
    var mean = sum / n;
    return { luma: mean, contrast: Math.sqrt(Math.max(0, sumSq / n - mean * mean)), grid: grid };
}

function gridDiff(a, b) {
    if (!a || !b || a.length !== b.length || !a.length) return 0;
    var s = 0;
    for (var i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
    return s / a.length;
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

/* Usability of one sampled moment, 0..1.
   A frame is worth nothing if it is essentially black, blown out, or a flat
   wash — those are the lens-warmup frames, fades and dead air that a montage
   should never open on. Past that, texture matters most and movement is a
   bonus: a static-but-detailed frame is still a fine cut target. */
function quality(s, activity) {
    if (!s) return 0;
    if (s.luma < 10 || s.luma > 248 || s.contrast < 6) return 0;
    var texture = clamp01(s.contrast / 40);
    var motion = clamp01(activity / 25);
    return clamp01(texture * (0.55 + 0.45 * motion));
}

async function profile(clip, opts) {
    opts = opts || {};
    var dur = clip && clip.duration;
    if (!(dur > 0.4) || !clip.file || typeof root.__videoThumbFallback !== 'function') return null;
    var t0 = Date.now();
    var count = sampleCount(dur);
    var res;
    try {
        res = await root.__videoThumbFallback({
            file: clip.file, videoEl: null, duration: dur,
            width: clip.w || 640, height: clip.h || 360,
            count: count, isMobile: !!opts.isMobile, slug: opts.slug || 'music-video-maker',
            frameWidth: 120,
            // FFmpeg-per-thumb is minutes of WASM for a nicety the render does
            // not need; if HTML5 seeking cannot read the file we simply plan
            // arithmetically, exactly as before this module existed.
            noFfmpeg: true
        });
    } catch (_) { res = null; }
    var frames = (res && res.frames) || [];
    if (frames.length < 3) {
        ae('feature_use', { f: 'scene_profile', ok: 0, reason: frames.length ? 'too-few' : 'no-frames', asked: count });
        return null;
    }
    var samples = [], prevGrid = null;
    for (var i = 0; i < frames.length; i++) {
        var s = stats(frames[i].imageData);
        if (!s) continue;
        var act = gridDiff(prevGrid, s.grid);
        prevGrid = s.grid;
        samples.push({
            t: frames[i].time, luma: +s.luma.toFixed(1), contrast: +s.contrast.toFixed(1),
            activity: +act.toFixed(1), q: +quality(s, act).toFixed(3)
        });
    }
    if (samples.length < 3) return null;
    // the first sample has no predecessor — give it the second's activity so it
    // is not penalised for being first
    if (samples.length > 1) samples[0].q = +quality(
        { luma: samples[0].luma, contrast: samples[0].contrast }, samples[1].activity).toFixed(3);
    var dead = samples.filter(function (s2) { return s2.q <= 0.02; }).length;
    ae('feature_use', {
        f: 'scene_profile', ok: 1, samples: samples.length, dead: dead,
        src: (res && res.source) || '', ms: Date.now() - t0
    });
    return { dur: dur, samples: samples };
}

function summary(p) {
    if (!p || !p.samples.length) return { samples: 0, dead: 0, usableFrac: 1 };
    var dead = p.samples.filter(function (s) { return s.q <= 0.02; }).length;
    return { samples: p.samples.length, dead: dead, usableFrac: 1 - dead / p.samples.length };
}

/* Usability at an arbitrary time — the nearest sample's score. */
function qualityAt(p, t) {
    if (!p || !p.samples.length) return 1;
    var best = 0, bd = Infinity;
    for (var i = 0; i < p.samples.length; i++) {
        var d = Math.abs(p.samples[i].t - t);
        if (d < bd) { bd = d; best = p.samples[i].q; }
    }
    return best;
}

root.MusicVideoScene = {
    profile: profile, qualityAt: qualityAt, summary: summary,
    _stats: stats, _quality: quality
};

// `stats` and `quality` are pure — no DOM, no canvas, an ImageData-shaped
// {data,width,height} in and numbers out. Exporting them for node keeps their
// thresholds honest: tests/js/music-video-scene.test.mjs runs them over frames
// pulled from real fixtures rather than trusting numbers that were picked by
// eye. Same pattern as image-blur-metric.js. No effect in the browser.
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { stats: stats, quality: quality, qualityAt: qualityAt, summary: summary };
}
})();
